import {
  Breadcrumbs,
  Button,
  Card,
  CardHeader,
  colors,
  FormControlLabel,
  Link,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import { makeStyles } from "@material-ui/styles";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import "prismjs/components/prism-clike";
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-javascript";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAlert } from "react-alert";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { GroupType } from "react-select";
import Editor from "react-simple-code-editor";

import { RefreshIcon } from "../components/icons/themes/windows10";
import SelectEventDefinition, {
  EventDefOption,
} from "../components/Dropdown/SelectEventDefinition";
import useEnvironment from "../hooks/use-environment";
import { useAllSaasDestinations } from "../hooks/useAllSaasDestinations";
import Label from "../Label";
import Navigation from "../Navigation";
import { StoreState } from "../store";
import { LoadedEventDefinition } from "../types/global";
import { delayAsync, makeRequest, userIsFPEmployee } from "../util";
import { DeliveryStatuses, Trace as TraceType } from "./types";
import { AxiosError } from "axios";
import { EventDefsState } from "../reducers/events";
import { EventTesterState } from "../reducers/eventTester";
import useDestinationById from "../hooks/useDestinationById";
import { allDestinationsData } from "./destinations";
import { DestinationCardTitle } from "../components/DestinationCardTitle";

// statusExplanation tries to return something suggestive as to what
// went wrong with an event trace. It expects to be given one of the
// delivery statuses as defined in `back/models/destination/delivery`.
function statusExplanation(status: string): string {
  switch (status) {
    case "freshpaint_internal_error":
      return "something is wrong on our end (we're working on it!)";
    case "invalid_event_payload":
      return "your event's JSON payload isn't valid";
    case "network_error":
    case "timeout":
      return "your destination wasn't reachable";
    case "transformation_error":
      return "a transformation you configured has an error";
    case "transformation_skip":
      return "a transformation indicated we should not send";
  }

  // Fallback to just sending the exact status key that we received
  return status;
}

async function makeTracedRequest(dest, event, eventDef, redacted) {
  return await makeRequest<{
    traces: [TraceType];
    statuses: DeliveryStatuses;
    deliveryMessage: string;
  }>("trace_integration", {
    dest_id: dest.id,
    event_def_id: eventDef?.value?.id,
    event: {
      event: eventDef?.value.name,
      properties: event,
    },
    redacted: redacted,
  });
}

async function attemptTestEvent(dest, eventDef, eventStr, redacted, alert) {
  let event;
  try {
    if (!eventStr) {
      throw new Error();
    }
    event = JSON.parse(eventStr);
  } catch (e) {
    alert.error("Event properties are not valid JSON");
    return;
  }

  await delayAsync(250);

  try {
    return await makeTracedRequest(dest, event, eventDef, redacted);
  } catch (error) {
    const err = error as AxiosError;
    if (err.response?.status === 403) {
      alert.error("You do not have permission to perform this action.");
    } else {
      alert.error(
        "Failed to process event. Make sure the event is properly formatted."
      );
    }
    console.log(error);
    Sentry.captureException(error);
  }
}

const freshpaintSystemEventOptions: Array<GroupType<EventDefOption>> = [
  {
    label: "Freshpaint System",
    options: [
      {
        label: "Identify",
        value: {
          id: "00000000-0000-0000-0000-000000000000",
          name: "$identify",
          description: "Identify a user",
          enabled_dests: {},
          enabled_client_destinations: {},
          enabled_server_destinations: {},
          segment_compatible: false,
          source: "server",
          type: "server",
          is_hidden: false,
          filter: {
            type: "nop",
            filters: [],
          },
          tags: {},
          snapshots: [],
        },
      },
      {
        label: "Page",
        value: {
          id: "00000000-0000-0000-0000-000000000000",
          name: "pageview",
          description: "Simulate a page view",
          enabled_dests: {},
          enabled_client_destinations: {},
          enabled_server_destinations: {},
          segment_compatible: false,
          source: "server",
          type: "server",
          is_hidden: false,
          filter: {
            type: "nop",
            filters: [],
          },
          tags: {},
          snapshots: [],
        },
      },
    ],
  },
];

const exampleIdentify = (ID) => `{
"token": "${ID}",
"time": ${Math.floor(Date.now() / 1000)},
"distinct_id": "jon.doe@example.com",
"$device_id": "00000000-1111-2222-AAAA-BBBBBBBBBBBB",
"$user_props": {
"firstname": "John",
"lastname": "Doe"
}
}`;

const exampleEvent = (ID) => `{
"distinct_id": "test-user-id",
"token": "${ID}",
"time": ${Math.floor(Date.now() / 1000)},
"test-property": 10
}`;

function DestinationEventTester({
  destinationSlug,
  destinationId,
  initialEvent = "{}",
  initialEventDefID,
  eventDefs,
  eventDefsLoading,
}: {
  destinationSlug: string;
  destinationId: string;
  initialEvent?: string;
  initialEventDefID?: string | null | undefined;
  eventDefs: LoadedEventDefinition[];
  eventDefsLoading: boolean;
}) {
  const initialEventDef = eventDefs.find((def) => def.id === initialEventDefID);

  const initialOption = initialEventDef && {
    label: initialEventDef.name,
    value: initialEventDef,
  };

  const alert = useAlert();
  const { ID } = useEnvironment();
  const [eventStr, setEventStr] = useState<string>(initialEvent);
  const [traces, setTraces] = useState<[TraceType] | null>(null);
  const [message, setMessage] = useState<JSX.Element | string | null>(null);
  const [issue, setIssue] = useState<string | null>(null);
  const [redacted, setRedacted] = useState<boolean>(true);
  const [selectedEventDef, setSelectedEventDef] = useState<
    EventDefOption | null | undefined
  >();
  const [isLoading, setLoading] = useState<boolean>(false);
  const dest = useDestinationById(destinationId);
  const destination = allDestinationsData[destinationSlug];

  const resetEventText = (newEventDef) => {
    const selectedEvent = newEventDef?.value;
    if (ID && selectedEvent) {
      selectedEvent.name === "$identify"
        ? setEventStr(exampleIdentify(ID))
        : setEventStr(exampleEvent(ID));
    }
  };

  // Because the eventDefs load asynchronously, we might not have loaded them
  // when the component first mounts: we want to trigger a render once event
  // Defs have loaded
  useEffect(() => {
    setSelectedEventDef(initialOption);
  }, [eventDefs]);

  if (!ID || !dest) {
    // Wait for the ID to return before we set the initial state of the event
    // string.
    return null;
  }

  const lastTrace = traces && traces[traces.length - 1];
  const statusCode = lastTrace && lastTrace.response.statusCode;
  const statusMessage = lastTrace && lastTrace.response.statusMessage;
  const statusDisplayMessage = lastTrace && `${statusCode} ${statusMessage}`;
  const statusColor =
    statusCode &&
    (200 <= statusCode && statusCode < 300
      ? colors.green[600]
      : colors.red[600]);

  const isFPEmployee = userIsFPEmployee();

  const changeEventDefSelection = (eventDef) => {
    setSelectedEventDef(eventDef);
    resetEventText(eventDef);
  };

  // If we get a response back from our HTTP call, we want
  // to respond and update the UI
  const processTestResponse = (response) => {
    if (response.data.traces && response.data.traces.length > 0) {
      setTraces(response.data.traces);
      setMessage(null);
      setIssue(null);
    } else if (
      response.data.statuses &&
      Object.keys(response.data.statuses).length > 0
    ) {
      setTraces(null);
      const reason = Object.keys(response.data.statuses)
        .map((status) => statusExplanation(status))
        .join("; ");

      // We only want to show this error when no traces can be
      // shown. The best error display will be the trace itself --
      // this alert is only intended in cases where there is no such
      // context.
      setMessage("Error testing event: " + reason);
      response.data.deliveryMessage
        ? setIssue("Issue: " + response.data.deliveryMessage)
        : setIssue(null);
    } else {
      setTraces(null);
      setMessage(
        <div>
          This event caused no action in the destination. The destination may
          not support this event type. Learn more in the&nbsp;
          <a
            className="underline"
            style={{ color: "blue" }}
            href="https://documentation.freshpaint.io/destinations"
            rel="noopener noreferrer"
            target="_blank"
          >
            Freshpaint documentation
          </a>
          .
        </div>
      );
      setIssue(null);
    }
  };

  return (
    <Card>
      <CardHeader
        title={
          <DestinationCardTitle
            titleSuffix="Event Tester"
            destination={destination}
            configuredDest={dest}
          />
        }
      />
      <div className="m-4 flex flex-1">
        <span className="mx-2 my-auto">Event: </span>
        <SelectEventDefinition
          value={selectedEventDef}
          prependOptions={freshpaintSystemEventOptions}
          onChange={changeEventDefSelection}
          className="flex-grow max-w-md"
          required
        />
      </div>
      <div className="my-4 mx-6 flex flex-col flex-1">
        <span>Event properties:</span>
        <Editor
          className="mt-2 border-2 bg-gray-100 px-2 py-3 text-gray-900 font-mono text-xs"
          padding={12}
          style={{ minHeight: 120 }}
          onValueChange={setEventStr}
          value={eventStr}
          highlight={(code) => highlight(code, languages.js)}
          readOnly={!selectedEventDef}
        />
      </div>
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-center mx-6 my-4">
          <Button
            color="primary"
            onClick={async () => {
              setLoading(true);
              try {
                let response = await attemptTestEvent(
                  dest,
                  selectedEventDef,
                  eventStr,
                  redacted,
                  alert
                );
                response && processTestResponse(response);
              } finally {
                setLoading(false);
              }
            }}
            variant="contained"
            disabled={!selectedEventDef || isLoading}
          >
            Test Event
          </Button>
          {isLoading && (
            <div className="px-3 py-2 font-bold text-lg flex items-center">
              <RefreshIcon className="animate-spin mx-auto" />
            </div>
          )}
          {traces && (
            <div className="ml-4">
              <Label color={statusColor}>{statusDisplayMessage}</Label>
            </div>
          )}
        </div>
        {isFPEmployee && (
          <FormControlLabel
            control={
              <Switch
                checked={redacted}
                color="secondary"
                title={`${redacted ? "Redacted" : "Unredacted"}`}
                name="redact-request"
                onClick={() => setRedacted(!redacted)}
                value={redacted}
              />
            }
            label="Redact request"
            labelPlacement="start"
            style={{ margin: "16px" }}
          />
        )}
      </div>
      {message && (
        <div className="mx-6 mb-3 flex flex-col flex-1">{message}</div>
      )}
      {issue && <div className="mx-6 mb-3 flex flex-col flex-1">{issue}</div>}
      {traces && traces.every((trace) => trace.requestCURL) && (
        <Table style={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell>Request</TableCell>
              <TableCell>Response</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {traces.map((trace, i) => (
              <Trace key={i} trace={trace} />
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

function Trace({ trace }: { trace: TraceType }) {
  return (
    <TableRow className="hover:bg-gray-100">
      <TableCell
        style={{
          padding: "32px",
          verticalAlign: "top",
          wordWrap: "break-word",
        }}
      >
        <pre className="text-xs break-words whitespace-pre-wrap">
          {trace.requestCURL}
        </pre>
      </TableCell>
      <TableCell
        style={{
          padding: "32px",
          verticalAlign: "top",
        }}
      >
        <pre className="text-xs break-words whitespace-pre-wrap">
          {JSON.stringify(trace.response, null, 2)}
        </pre>
      </TableCell>
    </TableRow>
  );
}

const useStyles = makeStyles(() => ({
  navigation: {
    paddingTop: 12,
  },
  card: {
    width: "50%",
    minWidth: 800,
    minHeight: 500,
  },
  lightText: {
    color: "#546E7A",
  },
  darkText: {
    color: "#263238",
  },
  breadcrumb: {
    marginTop: 5,
    marginBottom: 10,
  },
}));

const DestinationEventTesterWrapper = ({
  destinationSlug,
  destinationId,
}: {
  destinationSlug: string;
  destinationId: string;
}) => {
  const history = useHistory();
  const classes = useStyles();

  const allSaasDestinations = useAllSaasDestinations();

  // TODO CJ Perhaps we should redirect to the destination list if the destination
  // is not found

  const destination = useMemo(() => allSaasDestinations[destinationSlug], [
    allSaasDestinations,
    destinationSlug,
  ]);

  const category = useMemo(() => destination?.categoryDisplay(), [destination]);
  const goToCategory = useCallback(
    () => history.push(`/destinations/${category}`),
    [category, history]
  );

  const goToDestination = useCallback(
    () =>
      history.push(
        `/destinations/${category}/${destinationSlug}/${destinationId}`
      ),
    [category, history, destinationSlug, destinationId]
  );

  const { eventDefs: storeEventDefs, loading: eventDefsLoading } = useSelector<
    StoreState,
    EventDefsState
  >((state: StoreState) => state.eventDefs);

  const { event: storeEventState, eventDefID: storeEventDefID } = useSelector<
    StoreState,
    EventTesterState
  >((state: { eventTester: EventTesterState }) => state.eventTester);

  const lastPage = (history.location.state as any)?.from;
  const initialEvent =
    lastPage === "trace-details-view"
      ? JSON.stringify(storeEventState, null, 2)
      : "{}";
  const initialEventDefID =
    lastPage === "trace-details-view" ? storeEventDefID : undefined;

  return (
    <Navigation className={clsx(classes.navigation)}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        className={clsx(classes.breadcrumb)}
      >
        <Link color="inherit" onClick={() => history.push(`/destinations`)}>
          <Typography className={clsx(classes.lightText)} variant="overline">
            Destinations
          </Typography>
        </Link>
        <Link color="inherit" onClick={goToCategory}>
          <Typography className={clsx(classes.lightText)} variant="overline">
            {destination?.categoryDisplay().toUpperCase()}
          </Typography>
        </Link>
        <Link color="inherit" onClick={goToDestination}>
          <Typography className={clsx(classes.lightText)} variant="overline">
            {destination?.name.toUpperCase()}
          </Typography>
        </Link>
        <Typography className={clsx(classes.darkText)} variant="overline">
          Event Tester
        </Typography>
      </Breadcrumbs>
      <DestinationEventTester
        destinationSlug={destinationSlug}
        destinationId={destinationId}
        initialEvent={initialEvent}
        initialEventDefID={initialEventDefID}
        eventDefs={storeEventDefs}
        eventDefsLoading={eventDefsLoading}
      />
    </Navigation>
  );
};

export default DestinationEventTesterWrapper;
